'use client'

import type { InputWithValidation, RequestValidationEventDetail } from '../../types/EventDetailTypes'
import {
    type ContainersForValidatableInputs,
    getArrayOfValidatableInputsFromSource,
} from '../../helpers/getArrayOfValidatableInputsFromSource'
import { RefObject, useEffect } from 'react'
import { REQUEST_PARTIAL_VALIDATION_EVENT } from '../../constants/custom-event-names'
import { assertIsCustomEvent } from '../use-validate-schema/helpers/assertIsCustomEvent'

export type GroupsOfRelatedFields = string[][]

const supplementRequestValidationEvent = (input: InputWithValidation, fieldNames: string[], signal: AbortSignal) => {
    input.addEventListener(
        REQUEST_PARTIAL_VALIDATION_EVENT,
        (event) => {
            assertIsCustomEvent(event as CustomEvent)
            // eslint-disable-next-line -- I am of sound mind at the time of mutating this function parameter.
            ;(event as CustomEvent<RequestValidationEventDetail>).detail.relatedFields = fieldNames
        },
        { capture: true, signal: signal },
    )
}

/**
 * @description
 * adds the provided fieldNames to the relatedFields property of the RequestValidationEventDetail
 * For more info {@link ../UseForm.ts}
 *
 * @listens RequestPartialValidation
 * */
export const useAddRelatedFieldsToEventDetail = (
    source: NodeList | null | RefObject<ContainersForValidatableInputs | null>,
    groupsOfRelatedField?: GroupsOfRelatedFields,
    addInputsFromSourceToFirstGroup?: boolean,
) => {
    useEffect(() => {
        const inputs = getArrayOfValidatableInputsFromSource(source)
        if (!inputs) {
            return
        }
        const controller = new AbortController()
        const signal = controller.signal

        if (!groupsOfRelatedField && !addInputsFromSourceToFirstGroup) {
            return
        }

        const relatedFieldGroups = groupsOfRelatedField ?? []

        if (!!addInputsFromSourceToFirstGroup) relatedFieldGroups[0].push(...inputs.map((input) => input.name))

        relatedFieldGroups.forEach((group: string[]) => {
            const relatedInputs = inputs.filter((input) => group.some((fieldName: string) => fieldName === input.name))
            relatedInputs.forEach((input) => {
                supplementRequestValidationEvent(input, group, signal)
            })
        })

        return () => {
            controller.abort()
        }
    }, [source])
}
